import { Box, Card, CardActions, CardContent, Chip, Typography } from '@mui/material';
import React from 'react';

// Import new components
import { AddToCartButton } from './AddToCartButton';
import type { MarketplaceService } from '../../types/pricing';
import { SimplePricingDisplay } from '../pricing/SimplePricingDisplay';
import { GeographicRestrictionsBanner } from '../service/GeographicRestrictionsBanner';
import { InstallDateDisplay } from '../service/InstallDateDisplay';
import { ServicePreviewModal } from '../service/ServicePreviewModal';

// Import types

// Brand watermark styling - simplified for current data structure
// TODO: Add brand type detection when owner_tenant_id is available in service data

interface ServiceCardNewProps {
  service: MarketplaceService;
  onCardClick?: (service: MarketplaceService) => void;
}

const SelectBadge: React.FC = () => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 0.25,
      borderRadius: '999px',
      border: '1px solid #D4AF37',
      bgcolor: 'rgba(212, 175, 55, 0.12)',
      color: '#8c6a00',
      fontSize: '0.68rem',
      fontWeight: 700,
      lineHeight: 1,
      textTransform: 'uppercase',
      letterSpacing: 0.3,
      whiteSpace: 'nowrap',
    }}
  >
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2.5L14.95 8.48L21.55 9.44L16.78 14.08L17.9 20.64L12 17.54L6.1 20.64L7.22 14.08L2.45 9.44L9.05 8.48L12 2.5Z"
        fill="#D4AF37"
      />
    </svg>
    Select
  </Box>
);

/**
 * Get default term display for marketplace tiles
 * Shows the default term for contract services
 */
const getDefaultTermDisplay = (service: MarketplaceService): string => {
  // Use service.default_term_months or first available term
  const defaultTerm =
    service.default_term_months ||
    (service.contract_term_months && service.contract_term_months[0]);

  if (defaultTerm) {
    return `${defaultTerm} month term`;
  }
  return '';
};

/**
 * REDESIGNED: Service Card Component
 * Uses new pricing structure and components
 * Zero backward compatibility - complete rebuild
 */
export const ServiceCardNew: React.FC<ServiceCardNewProps> = ({
  service,
  onCardClick,
}) => {
  const logoSrc = service.brand_logo_url || service.brand_logo_path || null;
  // Show watermark for any service with a brand logo
  const showWatermark = !!logoSrc;

  const handleCardClick = () => {
    onCardClick?.(service);
  };

  const hasWinningPerk = !!service.winningPerk;

  // Debug winning perk data (only in development)
  React.useEffect(() => {
    // Removed console.log for production
  }, [service.id, service.name, service.winningPerk, hasWinningPerk]);

  return (
    <Card
      component="article"
      itemScope
      itemType="https://schema.org/Service"
      onClick={handleCardClick}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.3s ease-in-out',
        // 3D effect
        transform: 'perspective(1200px) rotateX(0deg)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        // Visual cue for services with perks
        ...(hasWinningPerk && {
          border: '2px solid #3b82f6',
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.18)',
        }),
        '&:hover': {
          transform: 'perspective(1200px) rotateX(-3deg) translateY(-6px)',
          boxShadow: hasWinningPerk
            ? '0 16px 32px rgba(59, 130, 246, 0.25)'
            : '0 16px 32px rgba(0,0,0,0.12)',
        },
        // Brand logo watermark
        ...(showWatermark && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 8,
            right: 8,
            width: '45%',
            height: '45%',
            backgroundImage: logoSrc ? "url(" + logoSrc + ")" : undefined,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            opacity: 0.3,
            zIndex: 2,
            pointerEvents: 'none',
            'aria-hidden': 'true',
          },
        }),
      }}
    >
      <CardContent sx={{ flexGrow: 1, position: 'relative', zIndex: 1, p: 3 }}>
        {/* Brand name + Select badge in top right corner */}
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
          {service.is_select === true && <SelectBadge />}
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, textAlign: 'right' }}>
            {service.brand_name}
          </Typography>
        </Box>

        {/* Service Header */}
        <Box sx={{ mb: 2, pr: 12 }}>
          {' '}
          {/* Add padding right to avoid brand name overlap */}
          <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
            <span itemProp="name">{service.name}</span>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {service.category}
            {/* Add subcategory if available */}
            {service.metadata?.subcategory && ` • ${service.metadata.subcategory}`}
          </Typography>
        </Box>

        {/* Geographic Restrictions */}
        {service.geographic_restrictions && (
          <GeographicRestrictionsBanner restrictions={service.geographic_restrictions} />
        )}

        {/* Pricing Display - Show only default variant */}
        <SimplePricingDisplay service={service} />

        {/* Term Display - Show default term for contract services */}
        {service.contract_term_months && service.contract_term_months.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Chip
              label={getDefaultTermDisplay(service)}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', color: '#3b82f6', borderColor: '#93c5fd', fontWeight: 500 }}
            />
          </Box>
        )}

        {/* Install Date Display */}
        <InstallDateDisplay service={service} />

        {/* Service Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 2,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <span itemProp="description">{service.description}</span>
        </Typography>
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', alignItems: 'center', px: 3, pb: 2, pt: 0 }}>
        {/* Left: perk chip + offer count */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
          {/* Winning Perk — typed chip matching buy box design language */}
          {service.winningPerk && (() => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const perk = service.winningPerk as any;
            const type: string = perk.perk_type || 'other';

            // Build label
            let label = '';
            let chipSx: object = {};

            if (type === 'rebate' && perk.rebate_amount) {
              const freq = perk.rebate_frequency === 'monthly' ? '/mo' : '';
              label = `💵 $${(perk.rebate_amount / 100).toFixed(0)}${freq} Rebate`;
              chipSx = { backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #86efac' };
            } else if (type === 'gift_card') {
              const brand = perk.gift_card_brand ? ` ${perk.gift_card_brand}` : '';
              const amt = perk.rebate_amount ? ` $${(perk.rebate_amount / 100).toFixed(0)}` : '';
              label = `🎁${amt}${brand} Gift Card`;
              chipSx = { backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' };
            } else if (type === 'free_service') {
              const name = perk.name && perk.name.length > 20 ? perk.name.substring(0, 18) + '…' : (perk.name || 'Free Service');
              label = `⭐ ${name}`;
              chipSx = { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd' };
            } else {
              label = perk.name ? (perk.name.length > 22 ? perk.name.substring(0, 20) + '…' : perk.name) : 'Special Offer';
              chipSx = { backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' };
            }

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                <Chip
                  label={label}
                  size="small"
                  variant="filled"
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    maxWidth: '100%',
                    ...chipSx,
                    '& .MuiChip-label': { px: 1.5 },
                  }}
                />
                {type === 'free_service' && perk?.metadata?.bundled_service_id && (
                  <Box sx={{ mt: 0.35 }}>
                    <ServicePreviewModal serviceId={perk.metadata.bundled_service_id} />
                  </Box>
                )}
              </Box>
            );
          })()}

          {/* Buying options count */}
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {(service as any).allPerks && (service as any).allPerks.length > 1 && (
            <Typography
              variant="caption"
              sx={{ color: '#3b82f6', fontWeight: 500, ml: 0.5, whiteSpace: 'nowrap' }}
            >
              · {(service as any).allPerks.length} offers
            </Typography>
          )}
        </Box>

        {/* Right: CTA button */}
        <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 1, flexShrink: 0 }}>
          <AddToCartButton
            serviceId={service.id}
            serviceName={service.name}
            variant="contained"
            size="small"
          />
        </Box>
      </CardActions>
    </Card>
  );
};
